import { isEmpty } from "lodash";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import {
  ApproversType,
  BillingCycle,
  IAbsenceType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

export const usePopulateForm = () => {
  const { setValue } = useFormContext();

  /**
   * Populates form fields with values from a parent absence type on `parentAbsenceId` field change in AbsenceTypeForm.
   *
   * This function copies configuration settings, billing cycle information,
   * approvers, entitled employees, and various configuration options from
   * a parent absence type to the current form using react-hook-form's setValue.
   *
   * @param parentAbsenceTypeData - The parent absence type object containing
   *   configuration data to be copied to the form
   */
  const populateAbsenceTypeFromParent = useCallback(
    (parentAbsenceTypeData: IAbsenceType) => {
      setValue("absenceGroupId", parentAbsenceTypeData.absenceGroupId);
      setValue("billingCycle", BillingCycle.MONTHLY);
      setValue(
        "billingCycleConfiguration.type",
        parentAbsenceTypeData.billingCycleConfiguration?.type
      );
      setValue(
        "configurationOptions.submitLimitations.prior",
        parentAbsenceTypeData.configurationOptions.submitLimitations.prior
      );
      setValue(
        "configurationOptions.submitLimitations.advance",
        parentAbsenceTypeData.configurationOptions.submitLimitations.advance
      );
      setValue(
        "approversType",
        isEmpty(parentAbsenceTypeData.approvers)
          ? ApproversType.AUTO
          : ApproversType.MANUAL
      );
      setValue("approvers", parentAbsenceTypeData.approvers);
      setValue(
        "entitledEmployees.type",
        parentAbsenceTypeData.entitledEmployees.type
      );
      setValue(
        "entitledEmployees.employmentTypes",
        parentAbsenceTypeData.entitledEmployees.employmentTypes
      );
      setValue(
        "entitledEmployees.employeeGroups",
        parentAbsenceTypeData.entitledEmployees.employeeGroups
      );
      setValue(
        "entitledEmployees.organizationUnits",
        parentAbsenceTypeData.entitledEmployees.organizationUnits
      );
      setValue(
        "entitledEmployees.costPlaces",
        parentAbsenceTypeData.entitledEmployees.costPlaces
      );
      setValue(
        "configurationOptions.absencesVisibleForOthers",
        parentAbsenceTypeData.configurationOptions.absencesVisibleForOthers
      );
      setValue(
        "configurationOptions.billInHours",
        parentAbsenceTypeData.configurationOptions.billInHours
      );
      setValue(
        "configurationOptions.billInCalendarDays",
        parentAbsenceTypeData.configurationOptions.billInCalendarDays
      );
      setValue(
        "configurationOptions.multipleRequestsPerDayAllowed",
        parentAbsenceTypeData.configurationOptions.multipleRequestsPerDayAllowed
      );
      setValue(
        "configurationOptions.modificationAllowed",
        parentAbsenceTypeData.configurationOptions.modificationAllowed
      );
      setValue(
        "configurationOptions.commentsAllowed",
        parentAbsenceTypeData.configurationOptions.commentsAllowed
      );
      setValue(
        "configurationOptions.justificationRequired",
        parentAbsenceTypeData.configurationOptions.justificationRequired
      );
      setValue(
        "configurationOptions.attachmentConfiguration",
        parentAbsenceTypeData.configurationOptions.attachmentConfiguration
      );
      setValue(
        "configurationOptions.replacerConfiguration",
        parentAbsenceTypeData.configurationOptions.replacerConfiguration
      );
      setValue(
        "notificationsConfiguration",
        parentAbsenceTypeData.notificationsConfiguration
      );
    },
    [setValue]
  );

  return { populateAbsenceTypeFromParent };
};
